<script lang="ts">
    import { postAction } from "../../../api"
    import { getCustomStatusIconObj, getOpCustomNote, getOpCustomStatus, tooltip } from "../../../helper"
    import { login, newNotification } from "../../../store"
    import { _ } from "svelte-i18n"
    import Icon from "../Icon.svelte"
    import Modal from "../Modal.svelte"
    import Select from "svelte-select"
    import CustomDealerStatusTooltip from "./CustomStatusTooltip.svelte"

    function updateStatusAndNote() {
        if (!status.value) return
        postAction(
            operation.collectionName == "operation"
                ? `setOperationCustom${type}Status`
                : `setOperationOutCustom${type}Status`,
            {
                operationId: operation.id,
                status: status.value,
                note: note,
            }
        ).then(() => {
            if (note !== getOpCustomNote(operation, type) && status.value !== getOpCustomStatus(operation, type)) {
                newNotification({
                    html: $_("components.table.fields.status.operationStatusUpdated", {
                        values: {
                            operationNumber: operation.number,
                            statusLabel: status.label,
                        },
                    }),
                    class: "success",
                })
            } else if (status.value !== getOpCustomStatus(operation, type)) {
                newNotification({
                    html: $_("components.table.fields.status.operationStatusOnlyUpdated", {
                        values: {
                            operationNumber: operation.number,
                            statusLabel: status.label,
                        },
                    }),
                    class: "success",
                })
            } else if (note !== getOpCustomNote(operation, type)) {
                newNotification({
                    html: $_("components.table.fields.status.operationNoteUpdated", {
                        values: {
                            operationNumber: operation.number,
                        },
                    }),
                    class: "success",
                })
            }
            if (type == "Internal") {
                operation.customInternalStatus = status.value
                operation.customInternalNote = note
            } else {
                operation.customDealerStatus = status.value
                operation.customDealerNote = note
            }
            showEditModal = false
        })
            .catch(() => {
                // `postAction` wirft bei einem Fehler (api.ts:230) und schaltet
                // die allgemeine Fehlermeldung ab (`showErrorMessage: false`).
                // Ohne dieses `catch` blieb die Ablehnung unbehandelt: der
                // Dialog blieb offen, es kam **keine** Meldung, und die
                // `unhandledrejection` landete als console.error in jedem
                // UI-Test. Siehe docs/bugs/open/… (B26).
                //
                // Der Dialog bleibt bewusst offen: die Eingabe geht so nicht
                // verloren, und der Nutzer kann es erneut versuchen.
                newNotification({
                    class: "error",
                    html: $_("components.table.fields.status.operationStatusNotSaved"),
                })
            })
    }

    export let operation: Operation | OperationOut, type: "Internal" | "Dealer"
    export let showEditModal = false
    const customStatusDefaultValue = "open"
    let status = {
            value: getOpCustomStatus(operation, type) || customStatusDefaultValue,
            label: getCustomStatusIconObj(operation)[getOpCustomStatus(operation, type) || customStatusDefaultValue]
                ?.title,
        },
        note = getOpCustomNote(operation, type) || ""
    function allowedToEdit() {
        return (
            (type == "Dealer" && !($login?.account.roles?.includes("A") || $login?.account.roles?.includes("R"))) ||
            (type == "Internal" && ($login?.account.roles?.includes("A") || $login?.account.roles?.includes("R")))
        )
    }
    function getTooltip() {
        return !allowedToEdit()
            ? {
                  content: $_("components.table.fields.status.noPermissionToEditStatus"),
              }
            : false
    }
    function closeModal() {
        showEditModal = false
        ;(status = {
            value: getOpCustomStatus(operation, type) || customStatusDefaultValue,
            // Das `?.` fehlt hier ursprünglich – die Initialisierung weiter
            // oben (Zeile 69) hat es. Sobald ein Dokument einen Statuswert
            // trägt, den es in `customStatusIconsType1/2` nicht gibt, wirft
            // das Schließen des Dialogs sonst einen `TypeError: Cannot read
            // properties of undefined (reading 'title')`; der Dialog bleibt
            // dann offen und der Vorgang ist unbedienbar (B27, gemessen –
            // der Console-Monitor protokolliert den `pageerror`).
            //
            // Solche Werte kommen real vor: das Schema begrenzt das Feld
            // nicht (freier String), und der API-Test `customNotes.spec.ts`
            // schreibt `status: "status"` – einen Wert, den es in den
            // Icon-Maps nicht gibt. Für den Bestand ist das Feld also nicht
            // durchgeprüft, deshalb gehört der robuste Zugriff hierher.
            label: getCustomStatusIconObj(operation)[getOpCustomStatus(operation, type) || customStatusDefaultValue]
                ?.title,
        }),
            (note = getOpCustomNote(operation, type) || "")
    }
    function insertTimestamp() {
        const timestamp = new Date().toLocaleString("de-DE", {
            weekday: "short",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
        const newLine = `${timestamp} (${
            $login?.account?.firstname || $_("components.table.fields.status.defaultFirstName")
        } ${$login?.account?.lastname || $_("components.table.fields.status.defaultLastName")})\n`
        note = `---- ${newLine}${note ? "\n" + note : ""}`

        setTimeout(() => {
            const textarea = document.querySelector("textarea")
            if (textarea) {
                // Place the cursor right after the new timestamp
                const position = newLine.length + 5 // Adjust position to account for additional characters in 'note'
                textarea.setSelectionRange(position, position)
                textarea.focus()
            }
        }, 100)
    }
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<style lang="less">
    :global button.stateButton,
    :global .stateButton {
        color: white !important;
        display: flex;
        justify-content: center;
        width: 22px;
        height: 22px;
        align-items: center;
        padding: 0px !important;
        &::before {
            display: none;
        }
        &::after {
            display: none;
        }
        &.Internal {
            border-bottom: 2px solid var(--renzrot);
        }
        svg {
            fill: white !important;
            color: white !important;
            background-color: transparent !important;
            transform: translate(1px, 1px);
        }
        &.open {
            background-color: var(--silver) !important;
        }
        &.lost {
            background-color: var(--renzrot) !important;
        }
        &.won {
            background-color: var(--stategreen) !important;
        }
        &.waitingForResponse {
            background-color: var(--stateyellow) !important;
        }
        &.followUp {
            background-color: var(--stateorange) !important;
        }
        &.locked {
            background-color: var(--statepurple) !important;
        }
        &.extended {
            width: 24px;
            height: 24px;
            border-radius: 5px;
            svg {
                width: 24px;
                height: 24px;
                transform: translate(1px, 1px) !important;
            }
        }
    }
    .wrapper {
        height: 30px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        position: relative;
        .note {
            top: -3px;
            right: -8px;
            position: absolute;
            height: 20px;
            width: 20px;
            z-index: 100;
            display: flex;
            justify-content: center;
            align-items: center;
        }
        .line {
            border-bottom: 4px solid var(--renzrot);
            height: 4px;
            width: 100%;
        }
    }
    .modal {
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding: 20px;
        align-items: flex-start;
        .status {
            display: flex;
            flex-direction: column;
            width: 100%;
            align-items: flex-start;
            p {
                margin: 0;
            }
        }
        .notes {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
            em {
                font-style: normal;
                font-weight: 700;
            }
        }
        .notes-input {
            width: 100%;
            textarea {
                padding: 18px 20px;
            }
        }
    }
</style>

{#key showEditModal}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
        class="wrapper"
        on:click|stopPropagation="{() => {
            showEditModal = true
        }}"
        use:tooltip="{{
            content: {
                component: CustomDealerStatusTooltip,
                props: {
                    operation,
                    type: type,
                },
            },
        }}"
    >
        {#if !!note}
            <div class="note">
                <Icon
                    size="20px"
                    props="{{
                        stroke: 'black',
                    }}"
                    color="#FFE600"
                    path="M0.5 1V0.500001H1H4H14.5C15.0304 0.500001 15.5391 0.710714 15.9142 1.08579C16.2893 1.46086 16.5 1.96957 16.5 2.5V11.5C16.5 12.0304 16.2893 12.5391 15.9142 12.9142C15.5391 13.2893 15.0304 13.5 14.5 13.5H2.5C1.96957 13.5 1.46086 13.2893 1.08579 12.9142C0.710714 12.5391 0.5 12.0304 0.5 11.5V1ZM5 4.5H4.5V5H5V4.5ZM5 6.75H4.5V7.25H5V6.75ZM5 9H4.5V9.5H5V9ZM12.5 4.5H7.5V5H12.5V4.5ZM12.5 6.75H7.5V7.25H12.5V6.75ZM10.25 9H7.5V9.5H10.25V9Z"
                />
            </div>
        {/if}

        <button data-cy="status-button-{type}" style="height: 22px !important;" class="stateButton btn {status.value}">
            <Icon path="{getCustomStatusIconObj(operation)[status.value]?.icon}" size="22px" />
        </button>

        {#if type == "Internal"}
            <div class="line"></div>
        {/if}
    </div>
{/key}
<Modal show="{showEditModal}" size="md" on:close="{closeModal}">
    <div slot="header">
        {$_("components.table.fields.status.operationTracking", {
            values: {
                operationNumber: operation.number,
            },
        })}
    </div>
    <div class="modal">
        <section class="status" data-cy="status-select" use:tooltip="{getTooltip()}">
            <p>{$_("components.table.fields.status.status")}</p>
            <Select
                inputAttributes="{{ autocomplete: 'off' }}"
                placeholder=""
                showChevron="{true}"
                disabled="{!allowedToEdit()}"
                items="{Object.entries(getCustomStatusIconObj(operation)).map(([key, value]) => ({
                    value: key,
                    label: value.title,
                }))}"
                value="{status}"
                on:change="{(e) => (status = e.detail)}"
                clearable="{false}"
            >
                <div slot="selection" style="display: flex; align-items: center; gap: 10px;" let:selection>
                    <div class="stateButton extended {selection.value}">
                        <Icon path="{getCustomStatusIconObj(operation)[selection?.value]?.icon}" size="24px" />
                    </div>
                    <span style="margin-left: 10px;"> {selection?.label}</span>
                </div>
                <div slot="item" let:item style="display: flex; align-items: center; gap: 10px;">
                    <div class="stateButton extended {item.value}">
                        <Icon path="{getCustomStatusIconObj(operation)[item?.value]?.icon}" size="24px" />
                    </div>
                    <span style="margin-left: 10px;"> {item?.label}</span>
                </div>
            </Select>
        </section>
        <section class="notes" data-cy="notes-timestamp-button" use:tooltip="{getTooltip()}">
            <em> {$_("components.table.fields.status.notes")}</em>
            <button
                class="btn btn-secondary"
                disabled="{!allowedToEdit()}"
                on:click|stopPropagation|preventDefault="{() => {
                    insertTimestamp()
                }}"
            >
                {$_("components.table.fields.status.insertTimestampLine")}
            </button>
        </section>
        <section data-cy="notes-input" class="notes-input" use:tooltip="{getTooltip()}">
            <textarea
                disabled="{!allowedToEdit()}"
                bind:value="{note}"
                placeholder="{$_('components.table.fields.status.notes')}"
                style="width: 100%; height: 100px;"
            ></textarea>
        </section>
    </div>
    <div slot="footer" use:tooltip="{getTooltip()}">
        <button
            data-cy="cancel-button"
            class="btn btn-secondary"
            on:click|stopPropagation|preventDefault="{() => {
                closeModal()
            }}"
        >
            {$_("general.cancel")}
        </button>
        <button
            disabled="{!allowedToEdit()}"
            class="btn"
            data-cy="save-button"
            on:click|stopPropagation|preventDefault="{updateStatusAndNote}"
            >{$_("general.save")}
        </button>
    </div>
</Modal>
